import { type JSX } from "react";
import {
  type ComponentState,
  type HealthResponse,
  useHealth,
} from "@via/api/health";

type Row = { key: string; label: string; state: ComponentState };

/**
 * Which rows to show, from what the probe actually reported.
 *
 * The agent hub is listed only when it is enabled, because every deployment today runs with it off
 * and readiness deliberately does not gate on a disabled hub. Showing a permanently disabled row
 * would teach people to ignore a row, which is the opposite of what a status page is for.
 */
export const componentRows = (health: HealthResponse): Row[] => {
  const rows: Row[] = [
    { key: "w", label: "Web server", state: health.details.w },
  ];

  if (health.details.t) {
    rows.push({ key: "t", label: "Secure tunnel", state: health.details.t });
  }
  if (health.details.s) {
    rows.push({ key: "s", label: "Interactive apps", state: health.details.s });
  }
  const hub = health.details.agentHub;
  if (hub?.enabled) {
    rows.push({
      key: "agentHub",
      label: "Agent hub",
      state: hub.listening && hub.lockHeld ? "up" : "down",
    });
  }
  return rows;
};

const STATE_LABEL: Record<ComponentState, string> = {
  up: "Operational",
  down: "Not responding",
  disabled: "Not enabled",
};

const STATE_CLASS: Record<ComponentState, string> = {
  up: "bg-green-50 text-green-700 ring-green-600/20",
  down: "bg-red-50 text-red-700 ring-red-600/20",
  disabled: "bg-gray-50 text-gray-600 ring-gray-500/20",
};

function StateBadge({ state }: { state: ComponentState }): JSX.Element {
  return (
    <span
      className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${STATE_CLASS[state]}`}>
      {STATE_LABEL[state]}
    </span>
  );
}

export default function Status(): JSX.Element {
  const { data, isPending } = useHealth();

  return (
    <div className="via-content-wrapper">
      <main className="mx-auto w-full max-w-2xl px-6 py-16 lg:px-8">
        <h1 className="via-h2 text-2xl font-bold tracking-tight sm:text-3xl">
          Platform status
        </h1>

        {isPending ? (
          <p className="mt-6 text-base leading-7 text-gray-600">
            Checking the platform…
          </p>
        ) : (
          <>
            <p
              className="mt-2 text-base leading-7 text-gray-600"
              data-testid="status-headline">
              {data?.state === "up"
                ? "All components are operational."
                : "Some components are not responding."}
            </p>

            <ul
              role="list"
              className="mt-8 divide-y divide-gray-900/5 border-t border-gray-900/10">
              {componentRows(data as HealthResponse).map(row => (
                <li
                  key={row.key}
                  data-testid={`status-row-${row.key}`}
                  className="flex items-center justify-between gap-x-6 py-4">
                  <span className="text-sm font-semibold leading-6 text-gray-900">
                    {row.label}
                  </span>
                  <StateBadge state={row.state} />
                </li>
              ))}
            </ul>

            <p className="mt-8 text-sm leading-6 text-gray-500">
              This page refreshes on its own every 30 seconds.
            </p>
          </>
        )}
      </main>
    </div>
  );
}
